<template>
    <Tooltip :placement="props.defaultTooltipPlacement" :style="colorStyle">
        <template #trigger="tooltipProps">
            <div
                class="flex flex-col gap-0.5 hover:bg-(--color-100) p-1 rounded-full cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-700"
                v-bind="tooltipProps"
                tabindex="0"
            >
                <div
                    v-if="props.status === 'Occupied-Prio-1'"
                    class="bg-(--color-opacity-100) rounded-full"
                    :class="sizeLocationDotClass"
                />
                <div
                    v-if="props.status === 'Occupied-Prio-2'"
                    class="flex bg-(--color-opacity-30) relative rounded-full"
                    :class="sizeLocationDotClass"
                >
                    <div
                        v-if="props.status === 'Occupied-Prio-2'"
                        class="bg-(--color-opacity-100) rounded-full size-1.75 absolute left-[0.09375rem] top-[0.09375rem]"
                    />
                </div>
                <div
                    v-if="props.status === 'Occupied-Prio-3'"
                    class="bg-(--color-opacity-30) rounded-full"
                    :class="sizeLocationDotClass"
                />
                <PartiallyOccupiedIcon
                    v-if="props.status === 'Partially-Occupied'"
                    class="text-(--color-opacity-100)"
                    :class="sizeLocationDotClass"
                />
                <OptionallyOccupiedIcon
                    v-if="props.status === 'Optionally-Occupied-Prio-1'"
                    class="text-(--color-opacity-100)"
                    :class="sizeLocationDotClass"
                />
                <div
                    v-if="props.status === 'Optionally-Occupied-Prio-2'"
                    class="flex bg-(--color-opacity-30) relative rounded-full"
                    :class="sizeLocationDotClass"
                >
                    <OptionallyOccupiedIcon
                        class="rounded-full bg-white absolute left-[0.09375rem] top-[0.09375rem] text-(--color-opacity-100) size-1.75"
                    />
                </div>
                <div
                    v-if="props.status === 'Optionally-Occupied-Prio-3'"
                    class="flex bg-(--color-opacity-30) relative rounded-full"
                    :class="sizeLocationDotClass"
                >
                    <OptionallyOccupiedIcon
                        class="rounded-full bg-white absolute left-px top-px text-(--color-opacity-30) size-2"
                    />
                </div>
                <div
                    v-if="props.status === 'Empty'"
                    class="border border-gray-500 rounded-full"
                    :class="sizeLocationDotClass"
                />
                <div v-if="props.status === 'Placeholder'" :class="sizeLocationDotClass" />
                <FlashIcon
                    v-if="props.status === 'Open-Conflicts'"
                    class="text-red-600"
                    :class="sizeLocationDotClass"
                />
                <FlashOffIcon
                    v-if="props.status === 'Resolved-Conflicts'"
                    class="text-(--color-opacity-100)"
                    :class="sizeLocationDotClass"
                />
            </div>
        </template>

        <template #text>
            <div class="flex flex-col">
                <div class="flex flex-row place-content-center text-gray-600 font-semibold">
                    {{ props.stateName }}
                </div>

                <div v-if="props.status !== 'Placeholder'" class="flex flex-row items-center gap-1">
                    <LocationOnIcon v-if="isOccupied" class="text-gray-500" />
                    <LocationOffIcon v-if="!isOccupied" class="text-gray-500" />

                    <span class="text-gray-500 font-light leading-none mb-px">→</span>

                    <div v-if="props.status === 'Occupied-Prio-1'" class="flex flex-row items-center gap-1">
                        <div class="bg-(--color-opacity-100) rounded-full" :class="sizeLocationDotClass" />
                        <div class="text-gray-700 font-semibold">{{ $t('occupied') }}</div>
                        <div class="text-gray-500 font-light">{{ $t('occupied-prio-1') }}</div>
                    </div>

                    <div v-if="props.status === 'Occupied-Prio-2'" class="flex flex-row items-center gap-1">
                        <div class="flex bg-(--color-opacity-30) relative rounded-full" :class="sizeLocationDotClass">
                            <div
                                v-if="props.status === 'Occupied-Prio-2'"
                                class="bg-(--color-opacity-100) rounded-full size-1.75 absolute left-[0.09375rem] top-[0.09375rem]"
                            />
                        </div>
                        <div class="text-gray-700 font-semibold">{{ $t('occupied') }}</div>
                        <div class="text-gray-500 font-light">{{ $t('occupied-prio-2') }}</div>
                    </div>

                    <div v-if="props.status === 'Occupied-Prio-3'" class="flex flex-row items-center gap-1">
                        <div class="bg-(--color-opacity-30) rounded-full" :class="sizeLocationDotClass" />
                        <div class="text-gray-700 font-semibold">{{ $t('occupied') }}</div>
                        <div class="text-gray-500 font-light">{{ $t('occupied-prio-3') }}</div>
                    </div>

                    <div v-if="props.status === 'Partially-Occupied'" class="flex flex-row items-center gap-1">
                        <PartiallyOccupiedIcon class="text-(--color-opacity-100)" :class="sizeLocationDotClass" />
                        <div class="text-gray-700 font-semibold">{{ $t('partially-occupied') }}</div>
                    </div>

                    <div v-if="props.status === 'Optionally-Occupied-Prio-1'" class="flex flex-row items-center gap-1">
                        <OptionallyOccupiedIcon class="text-(--color-opacity-100)" :class="sizeLocationDotClass" />
                        <div class="text-gray-700 font-semibold">{{ $t('optionally-occupied') }}</div>
                        <div class="text-gray-500 font-light">{{ $t('occupied-prio-1') }}</div>
                    </div>

                    <div v-if="props.status === 'Optionally-Occupied-Prio-2'" class="flex flex-row items-center gap-1">
                        <div class="flex bg-(--color-opacity-30) relative rounded-full" :class="sizeLocationDotClass">
                            <OptionallyOccupiedIcon
                                class="rounded-full bg-white absolute left-[0.09375rem] top-[0.09375rem] text-(--color-opacity-100) size-1.75"
                            />
                        </div>
                        <div class="text-gray-700 font-semibold">{{ $t('optionally-occupied') }}</div>
                        <div class="text-gray-500 font-light">{{ $t('occupied-prio-2') }}</div>
                    </div>

                    <div v-if="props.status === 'Optionally-Occupied-Prio-3'" class="flex flex-row items-center gap-1">
                        <div
                            v-if="props.status === 'Optionally-Occupied-Prio-3'"
                            class="flex bg-(--color-opacity-30) relative rounded-full"
                            :class="sizeLocationDotClass"
                        >
                            <OptionallyOccupiedIcon
                                class="rounded-full bg-white absolute left-px top-px text-(--color-opacity-30) size-2"
                            />
                        </div>
                        <div class="text-gray-700 font-semibold">{{ $t('optionally-occupied') }}</div>
                        <div class="text-gray-500 font-light">{{ $t('occupied-prio-3') }}</div>
                    </div>

                    <div v-if="props.status === 'Empty'" class="flex flex-row items-center gap-1">
                        <div class="border border-gray-500 rounded-full" :class="sizeLocationDotClass" />
                        <div class="text-gray-500 font-light">{{ $t('not-occupied') }}</div>
                    </div>

                    <div v-if="props.status === 'Open-Conflicts'" class="flex flex-row items-center gap-1">
                        <FlashIcon class="text-red-600" :class="sizeLocationDotClass" />
                        <div class="text-gray-700 font-semibold">{{ $t('open-conflicts') }}</div>
                    </div>

                    <div v-if="props.status === 'Resolved-Conflicts'" class="flex flex-row items-center gap-1">
                        <FlashOffIcon class="text-(--color-opacity-100)" :class="sizeLocationDotClass" />
                        <div class="text-gray-700 font-semibold">{{ $t('resolved-conflicts') }}</div>
                    </div>
                </div>
            </div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import FlashIcon from '../Icon/FlashIcon.vue';
import FlashOffIcon from '../Icon/FlashOffIcon.vue';
import LocationOffIcon from '../Icon/LocationOffIcon.vue';
import LocationOnIcon from '../Icon/LocationOnIcon.vue';
import OptionallyOccupiedIcon from '../Icon/OptionallyOccupiedIcon.vue';
import PartiallyOccupiedIcon from '../Icon/PartiallyOccupiedIcon.vue';
import { getAllocationColor100ForPhaseType, getBaseColorForPhaseType } from '../Timeline/phaseColors';
import { PhaseColor } from '../Timeline/Timeline.types';
import Tooltip from '../Tooltip/Tooltip.vue';

export type FederalStatesDotStatus =
    | 'Occupied-Prio-1'
    | 'Occupied-Prio-2'
    | 'Occupied-Prio-3'
    | 'Partially-Occupied'
    | 'Optionally-Occupied-Prio-1'
    | 'Optionally-Occupied-Prio-2'
    | 'Optionally-Occupied-Prio-3'
    | 'Empty'
    | 'Placeholder'
    // TODO | 'Not-Yet-Build'
    // TODO | 'Demolished'
    | 'Open-Conflicts'
    | 'Resolved-Conflicts';

type FederalStatesDotProps = {
    stateName: string;
    status: FederalStatesDotStatus;
    defaultTooltipPlacement: 'top' | 'bottom';
    color: Extract<
        PhaseColor,
        'External-Event-Category-Project' | 'External-Event-Category-Event' | 'External-Event-Category-External-Event'
    >;
};

const props = defineProps<FederalStatesDotProps>();

const sizeLocationDotClass = 'size-2.5';

const colorStyle = computed(() => {
    const baseColor = getBaseColorForPhaseType(props.color);

    return {
        '--color-opacity-100': `var(${baseColor})`,
        '--color-100': `var(${getAllocationColor100ForPhaseType(props.color)})`,
        '--color-opacity-30': `color-mix(in srgb, var(${baseColor}) 30%, white)`,
    };
});

const isOccupied = computed(
    () =>
        props.status === 'Occupied-Prio-1' ||
        props.status === 'Occupied-Prio-2' ||
        props.status === 'Occupied-Prio-3' ||
        props.status === 'Partially-Occupied' ||
        props.status === 'Optionally-Occupied-Prio-1' ||
        props.status === 'Optionally-Occupied-Prio-2' ||
        props.status === 'Optionally-Occupied-Prio-3' ||
        props.status === 'Open-Conflicts' ||
        props.status === 'Resolved-Conflicts',
);
</script>
